import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getSSOStatus,
  initiateSSOOnboardingAndRedirect,
  submitSamlIdpDetails,
  submitSamlMetadataUrl,
  getSSOStatusSelector,
  getIsInitiatingSSOSelector,
  getSSOErrorSelector,
  getPendingConnectionSelector,
  getIsSavingIdpSelector,
  runStytchAutoSetup,
  getIsUpdatingStytchOrgSelector,
  getStytchOrgErrorSelector,
  getStytchAutoSetupResultsSelector,
  getSSOConnectionsSelector,
  getActiveConnectionSelector,
  // Edit/Delete functionality
  editSamlConnection,
  deleteSamlConnection,
  getIsEditingSamlConnectionSelector,
  getIsDeletingSamlConnectionSelector,
  getSamlEditErrorSelector,
  getSamlDeleteErrorSelector,
} from "../store/brokerage";
import { getBrokerInformation } from "../store/broker";
import translations from "../utils/translations";
import DeleteConfirmationModal from "./DeleteConfirmationModal";

// Enhanced styles
const styles = {
  container: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    maxWidth: "800px",
    margin: "2rem auto",
    padding: "0 1rem",
    lineHeight: "1.6",
  },
  
  card: {
    border: "1px solid #e1e5e9",
    borderRadius: "12px",
    padding: "2rem",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    marginBottom: "1.5rem",
  },
  
  successCard: {
    border: "1px solid #22c55e",
    backgroundColor: "#f0fdf4",
  },
  
  pendingCard: {
    border: "1px solid #f59e0b",
    backgroundColor: "#fffbeb",
  },
  
  title: {
    fontSize: "1.5rem",
    fontWeight: "600",
    marginTop: "0",
    marginBottom: "1rem",
    color: "#1f2937",
  },
  
  successTitle: {
    color: "#16a34a",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  
  pendingTitle: {
    color: "#d97706",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  
  description: {
    color: "#6b7280",
    marginBottom: "1.5rem",
    fontSize: "1rem",
  },
  
  section: {
    marginBottom: "2rem",
  },
  
  sectionTitle: {
    fontSize: "1.1rem",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "0.75rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  
  stepNumber: {
    backgroundColor: "#3b82f6",
    color: "white",
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.875rem",
    fontWeight: "600",
    flexShrink: 0,
  },
  
  configList: {
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "1.5rem",
    marginBottom: "1.5rem",
  },
  
  configItem: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "1rem",
    paddingBottom: "1rem",
    borderBottom: "1px solid #e2e8f0",
  },
  
  configItemLast: {
    marginBottom: 0,
    paddingBottom: 0,
    borderBottom: "none",
  },
  
  configLabel: {
    fontSize: "0.875rem",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "0.25rem",
  },
  
  configValue: {
    fontFamily: "Monaco, 'Cascadia Code', 'Roboto Mono', monospace",
    fontSize: "0.875rem",
    color: "#1f2937",
    backgroundColor: "#ffffff",
    padding: "0.5rem",
    border: "1px solid #d1d5db",
    borderRadius: "4px",
    wordBreak: "break-all",
  },
  
  copyButton: {
    marginTop: "0.5rem",
    padding: "0.375rem 0.75rem",
    fontSize: "0.75rem",
    fontWeight: "500",
    color: "#3b82f6",
    backgroundColor: "transparent",
    border: "1px solid #3b82f6",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "all 0.2s",
    alignSelf: "flex-start",
  },
  
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "1.5rem",
  },
  
  formGroup: {
    display: "flex",
    flexDirection: "column",
  },
  
  label: {
    fontSize: "0.875rem",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "0.5rem",
  },
  
  input: {
    padding: "0.75rem",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "0.875rem",
    transition: "border-color 0.2s",
    fontFamily: "inherit",
  },
  
  textarea: {
    padding: "0.75rem",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "0.875rem",
    transition: "border-color 0.2s",
    fontFamily: "Monaco, 'Cascadia Code', 'Roboto Mono', monospace",
    resize: "vertical",
    minHeight: "120px",
  },
  
  helpText: {
    fontSize: "0.75rem",
    color: "#6b7280",
    marginTop: "0.25rem",
  },
  
  primaryButton: {
    padding: "0.75rem 1.5rem",
    backgroundColor: "#3b82f6",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "0.875rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
  },
  
  primaryButtonHover: {
    backgroundColor: "#2563eb",
  },
  
  primaryButtonDisabled: {
    backgroundColor: "#9ca3af",
    cursor: "not-allowed",
  },
  
  secondaryButton: {
    padding: "0.75rem 1.5rem",
    backgroundColor: "transparent",
    color: "#374151",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "0.875rem",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  
  errorText: {
    color: "#dc2626",
    fontSize: "0.875rem",
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: "6px",
    padding: "0.75rem",
    marginTop: "1rem",
  },
  
  loadingText: {
    color: "#6b7280",
    fontStyle: "italic",
    textAlign: "center",
    padding: "2rem",
  },
  
  warningBox: {
    backgroundColor: "#fffbeb",
    border: "1px solid #fed7aa",
    borderRadius: "6px",
    padding: "1rem",
    marginBottom: "1.5rem",
  },
  
  warningTitle: {
    color: "#d97706",
    fontWeight: "600",
    fontSize: "0.875rem",
    marginBottom: "0.5rem",
  },
  
  warningText: {
    color: "#92400e",
    fontSize: "0.875rem",
  },

  // Edit/Delete UI styles - Unique prefixed classes to avoid conflicts
  samlEditDeleteButtonGroup: {
    marginTop: "1.5rem",
    paddingTop: "1.5rem",
    borderTop: "1px solid #e5e7eb",
  },
  
  samlEditButtonRow: {
    display: "flex",
    gap: "0.75rem",
    alignItems: "center",
  },

  samlEditButton: {
    padding: "0.75rem 1.25rem",
    backgroundColor: "#3b82f6",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "0.875rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.2s",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },

  samlEditButtonHover: {
    backgroundColor: "#2563eb",
  },

  samlDeleteButton: {
    padding: "0.75rem 1.25rem",
    backgroundColor: "#dc2626",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "0.875rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.2s",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },

  samlDeleteButtonHover: {
    backgroundColor: "#b91c1c",
  },

  samlEditForm: {
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "2rem",
    marginTop: "1.5rem",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
  },

  samlEditFormTitle: {
    fontSize: "1.125rem",
    fontWeight: "700",
    color: "#374151",
    marginBottom: "1.5rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    paddingBottom: "1rem",
    borderBottom: "1px solid #e5e7eb",
  },

  samlEditFormGroup: {
    marginBottom: "1.75rem",
    display: "flex",
    flexDirection: "column",
  },

  samlEditLabel: {
    fontSize: "0.9rem",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "0.75rem",
    letterSpacing: "0.025em",
  },

  samlEditInput: {
    width: "100%",
    padding: "0.875rem 1rem",
    border: "2px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "0.875rem",
    transition: "all 0.2s ease-in-out",
    fontFamily: "inherit",
    backgroundColor: "#ffffff",
    boxSizing: "border-box",
  },

  samlEditInputFocus: {
    borderColor: "#3b82f6",
    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    outline: "none",
  },

  samlEditTextarea: {
    width: "100%",
    padding: "0.875rem 1rem",
    border: "2px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "0.875rem",
    transition: "all 0.2s ease-in-out",
    fontFamily: "Monaco, 'Cascadia Code', 'Roboto Mono', monospace",
    resize: "vertical",
    minHeight: "140px",
    backgroundColor: "#ffffff",
    boxSizing: "border-box",
  },

  samlEditTextareaFocus: {
    borderColor: "#3b82f6",
    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    outline: "none",
  },

  samlEditFormActions: {
    display: "flex",
    gap: "1rem",
    justifyContent: "flex-end",
    marginTop: "2rem",
    paddingTop: "1.5rem",
    borderTop: "1px solid #e5e7eb",
  },

  saveButton: {
    padding: "0.75rem 1.5rem",
    backgroundColor: "#16a34a",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "0.875rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.2s",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },

  saveButtonHover: {
    backgroundColor: "#15803d",
  },

  saveButtonDisabled: {
    backgroundColor: "#9ca3af",
    cursor: "not-allowed",
  },

  cancelButton: {
    padding: "0.75rem 1.5rem",
    backgroundColor: "#f3f4f6",
    color: "#374151",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "0.875rem",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s",
  },

  cancelButtonHover: {
    backgroundColor: "#e5e7eb",
  },

  buttonDisabled: {
    backgroundColor: "#9ca3af",
    cursor: "not-allowed",
    opacity: 0.6,
  },

  loadingSpinner: {
    width: "16px",
    height: "16px",
    border: "2px solid transparent",
    borderTop: "2px solid currentColor",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
};

function SSOManagement() {
  const dispatch = useDispatch();

  // Language preference setup
  const broker = useSelector(getBrokerInformation);
  const languagepreference = broker.information.languagepreference;
  const ssoTranslations = translations[languagepreference].sso;

  const ssoStatus = useSelector(getSSOStatusSelector);
  const isInitiating = useSelector(getIsInitiatingSSOSelector);
  const ssoError = useSelector(getSSOErrorSelector);
  const pendingConn = useSelector(getPendingConnectionSelector);
  const isSavingIdp = useSelector(getIsSavingIdpSelector);
  const isRunningAutoSetup = useSelector(getIsUpdatingStytchOrgSelector);
  const autoSetupError = useSelector(getStytchOrgErrorSelector);
  const autoSetupResults = useSelector(getStytchAutoSetupResultsSelector);
  const ssoConnections = useSelector(getSSOConnectionsSelector);
  const activeConnection = useSelector(getActiveConnectionSelector);
  
  // Edit/Delete selectors
  const isEditingConnection = useSelector(getIsEditingSamlConnectionSelector);
  const isDeletingConnection = useSelector(getIsDeletingSamlConnectionSelector);
  const samlEditError = useSelector(getSamlEditErrorSelector);
  const samlDeleteError = useSelector(getSamlDeleteErrorSelector);

  const [form, setForm] = useState({
    idp_sso_url: "",
    idp_entity_id: "",
    x509_certificate: "",
    idp_slo_url: "", // Optional Single Logout URL
    attribute_mapping: { 
      email: "email",
      first_name: "firstName", // or "givenName"
      last_name: "lastName"    // or "surname"
    },
  });

  // Metadata URL specific state
  const [metadataUrlForm, setMetadataUrlForm] = useState({
    metadata_url: "",
  });
  
  const [configurationMode, setConfigurationMode] = useState("manual"); // "metadata" or "manual"
  const [metadataUrlSuccess, setMetadataUrlSuccess] = useState(null); // Track successful metadata URL processing

  const [copiedField, setCopiedField] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    spDetails: false,
    idpDetails: false,
    connectionInfo: true
  });

  // Edit/Delete state
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    idp_sso_url: "",
    idp_entity_id: "",
    x509_certificate: "",
    idp_slo_url: "",
    attribute_mapping: {
      email: "email",
      first_name: "firstName",
      last_name: "lastName"
    }
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    dispatch(getSSOStatus());
  }, [dispatch]);

  // Debug effect to compare data sources when both are available
  useEffect(() => {
    if (metadataUrlSuccess?.extractedData && pendingConn) {
      console.log('=== COMPARING BOTH DATA SOURCES ===');
      const initialData = extractSamlData(metadataUrlSuccess.extractedData);
      const statusData = extractSamlData(pendingConn);
      
      console.log('Initial submit data:', initialData);
      console.log('Status endpoint data:', statusData);
      console.log('Data sources match:', JSON.stringify(initialData) === JSON.stringify(statusData));
      console.log('=====================================');
    }
  }, [metadataUrlSuccess, pendingConn]);

  // Auto-refresh after successful metadata URL processing
  useEffect(() => {
    if (ssoStatus === "pending" && !isSavingIdp && metadataUrlSuccess) {
      // Clear the success message after 5 seconds
      const timer = setTimeout(() => {
        setMetadataUrlSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [ssoStatus, isSavingIdp, metadataUrlSuccess]);

  // Pre-fill form when page loads with existing pending connection that has metadata
  useEffect(() => {
    if (pendingConn && pendingConn.idp_sso_url && pendingConn.idp_entity_id && 
        !form.idp_sso_url && !form.idp_entity_id) {
      
      console.log('=== STATUS ENDPOINT DATA SOURCE ===');
      console.log('Pending connection data:', pendingConn);
      console.log('SSO URL:', pendingConn.idp_sso_url);
      console.log('Entity ID:', pendingConn.idp_entity_id);
      console.log('Certificates:', pendingConn.verification_certificates);
      console.log('Certificate content:', pendingConn.verification_certificates?.[0]?.certificate?.substring(0, 100) + '...');
      
      // Use standardized extraction for consistency
      const samlData = extractSamlData(pendingConn);
      if (samlData) {
        setForm(prevForm => ({
          ...prevForm,
          idp_sso_url: samlData.idp_sso_url,
          idp_entity_id: samlData.idp_entity_id,
          x509_certificate: samlData.x509_certificate,
          idp_slo_url: samlData.idp_slo_url,
        }));
      }
      
      console.log('Form after pre-fill (refresh):', {
        idp_sso_url: pendingConn.idp_sso_url,
        idp_entity_id: pendingConn.idp_entity_id,
        has_certificate: !!pendingConn.verification_certificates?.[0]?.certificate
      });
      
      // Set configuration mode to manual so form is visible
      setConfigurationMode("manual");
    }
  }, [pendingConn, form.idp_sso_url, form.idp_entity_id]);

  const handleEnableSSO = () => {
    dispatch(initiateSSOOnboardingAndRedirect());
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmitIdp = () => {
    if (!pendingConn?.connection_id) return;

    dispatch(
      submitSamlIdpDetails({
        connection_id: pendingConn.connection_id,
        idp_sso_url: form.idp_sso_url,
        idp_entity_id: form.idp_entity_id,
        x509_certificate: form.x509_certificate,
        idp_slo_url: form.idp_slo_url,
        attribute_mapping: form.attribute_mapping,
      })
    );
  };

  const handleMetadataUrlChange = (e) => {
    const { name, value } = e.target;
    setMetadataUrlForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmitMetadataUrl = async () => {
    if (!pendingConn?.connection_id || !metadataUrlForm.metadata_url.trim()) return;

    console.log('Submitting metadata URL:', {
      connection_id: pendingConn.connection_id,
      metadata_url: metadataUrlForm.metadata_url.trim(),
      fullUrl: `/api/brokerages/saml/connection/${pendingConn.connection_id}/metadata-url`
    });

    const result = await dispatch(
      submitSamlMetadataUrl({
        connection_id: pendingConn.connection_id,
        metadata_url: metadataUrlForm.metadata_url.trim(),
      })
    );

    // Set success message and refresh status
    if (result.type.includes('samlMetadataUrlSaved')) {
      const extractedData = result.payload.connection;
      
      console.log('=== INITIAL SUBMIT DATA SOURCE ===');
      console.log('Full result payload:', result.payload);
      console.log('Extracted connection data:', extractedData);
      console.log('SSO URL:', extractedData.idp_sso_url);
      console.log('Entity ID:', extractedData.idp_entity_id);
      console.log('Certificates:', extractedData.verification_certificates);
      console.log('Certificate content:', extractedData.verification_certificates?.[0]?.certificate?.substring(0, 100) + '...');
      
      setMetadataUrlSuccess({
        url: metadataUrlForm.metadata_url.trim(),
        extractedData: extractedData
      });
      
      // Pre-fill the manual form with extracted data using standardized extraction
      const samlData = extractSamlData(extractedData);
      if (samlData) {
        setForm(prevForm => ({
          ...prevForm,
          idp_sso_url: samlData.idp_sso_url || prevForm.idp_sso_url,
          idp_entity_id: samlData.idp_entity_id || prevForm.idp_entity_id,
          x509_certificate: samlData.x509_certificate || prevForm.x509_certificate,
          idp_slo_url: samlData.idp_slo_url || prevForm.idp_slo_url,
        }));
      }
      
      console.log('Form after pre-fill:', {
        idp_sso_url: extractedData.idp_sso_url,
        idp_entity_id: extractedData.idp_entity_id,
        has_certificate: !!extractedData.verification_certificates?.[0]?.certificate
      });
      
      // Switch to manual mode to show pre-filled form
      setConfigurationMode("manual");
      
      // Auto-refresh SSO status after a brief delay to get latest state
      setTimeout(() => {
        dispatch(getSSOStatus());
      }, 1000);
    }
  };

  const handleConfigurationModeChange = (mode) => {
    setConfigurationMode(mode);
    // Clear any existing errors when switching modes
    // The global ssoError will still be cleared by the reducer when starting new operations
  };

  const handleRefreshStatus = () => {
    dispatch(getSSOStatus());
  };

  // Helper to check if field was pre-filled from metadata
  const isFieldFromMetadata = (fieldName) => {
    const hasMetadataData = (metadataUrlSuccess?.extractedData || 
                           (pendingConn?.idp_sso_url && pendingConn?.idp_entity_id)) && 
                           configurationMode === "manual" && 
                           ["idp_sso_url", "idp_entity_id", "x509_certificate"].includes(fieldName);
    return hasMetadataData;
  };

  // Helper to get the source data (either from success state or pending connection)
  const getMetadataSource = () => {
    return metadataUrlSuccess?.extractedData || pendingConn;
  };

  // Standardized data extraction helper to handle both data sources consistently
  const extractSamlData = (sourceData) => {
    if (!sourceData) return null;
    
    return {
      idp_sso_url: sourceData.idp_sso_url,
      idp_entity_id: sourceData.idp_entity_id,
      x509_certificate: sourceData.verification_certificates?.[0]?.certificate || '',
      idp_slo_url: sourceData.idp_slo_url || '',
      hasMetadata: !!(sourceData.idp_sso_url && sourceData.idp_entity_id)
    };
  };

  const handleAutoSetup = () => {
    if (window.confirm(
      "This will create Stytch organizations for all brokerages and register brokers as members. " +
      "This action should only be run once during initial setup. Are you sure?"
    )) {
      dispatch(runStytchAutoSetup());
    }
  };

  // Edit/Delete handlers
  const handleEditConnection = () => {
    if (activeConnection) {
      // Pre-fill edit form with current connection data
      setEditForm({
        idp_sso_url: activeConnection.idp_sso_url || "",
        idp_entity_id: activeConnection.idp_entity_id || "",
        x509_certificate: activeConnection.x509_certificate || "",
        idp_slo_url: activeConnection.idp_slo_url || "",
        attribute_mapping: activeConnection.attribute_mapping || {
          email: "email",
          first_name: "firstName", 
          last_name: "lastName"
        }
      });
      setIsInEditMode(true);
    }
  };

  const handleSaveEdit = () => {
    if (activeConnection && editForm.idp_sso_url && editForm.idp_entity_id && editForm.x509_certificate) {
      dispatch(editSamlConnection({
        connection_id: activeConnection.connection_id,
        idp_sso_url: editForm.idp_sso_url,
        idp_entity_id: editForm.idp_entity_id,
        x509_certificate: editForm.x509_certificate,
        idp_slo_url: editForm.idp_slo_url,
        attribute_mapping: editForm.attribute_mapping
      }));
    }
  };

  const handleCancelEdit = () => {
    setIsInEditMode(false);
    setEditForm({
      idp_sso_url: "",
      idp_entity_id: "",
      x509_certificate: "",
      idp_slo_url: "",
      attribute_mapping: {
        email: "email",
        first_name: "firstName",
        last_name: "lastName"
      }
    });
  };

  const handleDeleteConnection = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (activeConnection) {
      dispatch(deleteSamlConnection(activeConnection.connection_id));
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('attribute_mapping.')) {
      const attributeKey = name.split('.')[1];
      setEditForm(prev => ({
        ...prev,
        attribute_mapping: {
          ...prev.attribute_mapping,
          [attributeKey]: value
        }
      }));
    } else {
      setEditForm(prev => ({ ...prev, [name]: value }));
    }
  };

  // Effect to close delete modal on successful operations
  React.useEffect(() => {
    if (!isDeletingConnection && showDeleteModal && ssoStatus === 'not_configured') {
      // Delete completed successfully
      setShowDeleteModal(false);
    }
  }, [isDeletingConnection, showDeleteModal, ssoStatus]);

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const maskSensitiveData = (data, showChars = 8) => {
    if (!data || data.length <= showChars * 2) return data;
    const start = data.slice(0, showChars);
    const end = data.slice(-showChars);
    const masked = '*'.repeat(Math.min(data.length - showChars * 2, 20));
    return `${start}${masked}${end}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const renderConfigItem = (label, value, field, helpText = null) => (
    <div style={field === 'nameid_format' ? {...styles.configItem, ...styles.configItemLast} : styles.configItem}>
      <div style={styles.configLabel}>{label}</div>
      <div style={styles.configValue}>{value}</div>
      <button
        style={styles.copyButton}
        onClick={() => copyToClipboard(value, field)}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#eff6ff'}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
      >
        {copiedField === field ? '✓ ' + ssoTranslations.copied : ssoTranslations.copy}
      </button>
      {helpText && <div style={styles.helpText}>{helpText}</div>}
    </div>
  );

  const renderPending = () => {
    if (!pendingConn) return null;
    
    return (
      <div style={{...styles.card, ...styles.pendingCard}}>
        <h3 style={styles.pendingTitle}>
          <div style={{...styles.stepNumber, backgroundColor: '#f59e0b'}}>2</div>
          {ssoTranslations.completeSAMLConfig}
        </h3>
        
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <span style={{...styles.stepNumber, fontSize: '0.75rem'}}>A</span>
            {ssoTranslations.shareValues}
          </div>
          <p style={styles.description}>
            {ssoTranslations.itAdminDescription}
          </p>
          
          <div style={styles.configList}>
            {renderConfigItem(
              ssoTranslations.singleSignOnURL,
              pendingConn.acs_url,
              'acs_url',
              ssoTranslations.acsURLHelp
            )}
            {renderConfigItem(
              ssoTranslations.audienceURI,
              pendingConn.audience_uri,
              'audience_uri',
              ssoTranslations.audienceURIHelp
            )}
            {renderConfigItem(
              ssoTranslations.nameidFormat,
              pendingConn.nameid_format,
              'nameid_format',
              ssoTranslations.nameidFormatHelp
            )}
          </div>
        </div>

        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <span style={{...styles.stepNumber, fontSize: '0.75rem'}}>B</span>
            {ssoTranslations.enterIdpDetails}
          </div>
          <p style={styles.description}>
            {ssoTranslations.configureSSO}
          </p>

          {/* Configuration Mode Toggle */}
          <div style={{
            display: 'flex', 
            gap: '1rem', 
            marginBottom: '1.5rem',
            padding: '1rem',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <button
              type="button"
              onClick={() => handleConfigurationModeChange("metadata")}
              style={{
                ...styles.secondaryButton,
                ...(configurationMode === "metadata" ? {
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  borderColor: '#3b82f6'
                } : {})
              }}
            >
              🔗 {ssoTranslations.useMetadataURL}
            </button>
            <button
              type="button"
              onClick={() => handleConfigurationModeChange("manual")}
              style={{
                ...styles.secondaryButton,
                ...(configurationMode === "manual" ? {
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  borderColor: '#3b82f6'
                } : {})
              }}
            >
              ⚙️ {ssoTranslations.manualConfiguration}
            </button>
          </div>

          {configurationMode === "metadata" ? (
            // Metadata URL Configuration
            <div>
              <div style={styles.warningBox}>
                <div style={styles.warningTitle}>📋 {ssoTranslations.metadataURLSetup}</div>
                <div style={styles.warningText}>
                  {ssoTranslations.metadataURLDescription}
                </div>
              </div>

              <div style={styles.form}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{ssoTranslations.identityProviderMetadataURL} *</label>
                  <input
                    name="metadata_url"
                    value={metadataUrlForm.metadata_url}
                    onChange={handleMetadataUrlChange}
                    required
                    placeholder={ssoTranslations.metadataURLPlaceholder}
                    style={styles.input}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                  <div style={styles.helpText}>
                    {ssoTranslations.metadataURLHelp}
                  </div>
                </div>

                {/* Success Feedback */}
                {metadataUrlSuccess && (
                  <div style={{
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: '6px',
                    padding: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <div style={{fontSize: '0.875rem', color: '#15803d'}}>
                      <strong>✅ {ssoTranslations.metadataURLProcessedSuccessfully}</strong>
                      <br/>
                      <div style={{marginTop: '0.5rem'}}>
                        <strong>{ssoTranslations.extractedConfiguration}</strong>
                        <ul style={{marginTop: '0.25rem', paddingLeft: '1rem'}}>
                          {metadataUrlSuccess.extractedData?.idp_sso_url && (
                            <li><strong>{ssoTranslations.ssoURL}</strong> {metadataUrlSuccess.extractedData.idp_sso_url}</li>
                          )}
                          {metadataUrlSuccess.extractedData?.idp_entity_id && (
                            <li><strong>{ssoTranslations.entityID}</strong> {metadataUrlSuccess.extractedData.idp_entity_id}</li>
                          )}
                          {metadataUrlSuccess.extractedData?.verification_certificates?.length > 0 && (
                            <li><strong>{ssoTranslations.certificateImportedSuccessfully}</strong></li>
                          )}
                        </ul>
                        <div style={{marginTop: '0.5rem'}}>
                          <button
                            onClick={handleRefreshStatus}
                            style={{
                              ...styles.secondaryButton,
                              fontSize: '0.75rem',
                              padding: '0.375rem 0.75rem'
                            }}
                          >
                            🔄 Refresh Status
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {ssoError && (
                  <div style={styles.errorText}>
                    <strong>{ssoTranslations.configurationError}</strong> {ssoError}
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleSubmitMetadataUrl}
                  disabled={isSavingIdp || !metadataUrlForm.metadata_url.trim()}
                  style={{
                    ...styles.primaryButton,
                    ...((isSavingIdp || !metadataUrlForm.metadata_url.trim()) ? styles.primaryButtonDisabled : {})
                  }}
                  onMouseEnter={!isSavingIdp ? (e) => e.target.style.backgroundColor = styles.primaryButtonHover.backgroundColor : null}
                  onMouseLeave={!isSavingIdp ? (e) => e.target.style.backgroundColor = styles.primaryButton.backgroundColor : null}
                >
                  {isSavingIdp ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid transparent',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      {ssoTranslations.processingMetadata}
                    </>
                  ) : (
                    <>🚀 {ssoTranslations.configureWithMetadataURL}</>
                  )}
                </button>
              </div>
            </div>
          ) : (
            // Manual Configuration
            <div>
              <div style={styles.warningBox}>
                <div style={styles.warningTitle}>⚠️ {ssoTranslations.important}</div>
                <div style={styles.warningText}>
                  {ssoTranslations.emailAttributeWarning}
                </div>
              </div>

              <div style={styles.form}>
            

            <div style={styles.formGroup}>
              <label style={styles.label}>
                {ssoTranslations.idpSingleSignOnURL} * 
                {isFieldFromMetadata("idp_sso_url") && (
                  <span style={{color: '#16a34a', fontSize: '0.75rem', marginLeft: '0.5rem'}}>
                    ✅ {ssoTranslations.fromMetadataURL}
                  </span>
                )}
              </label>
              <input
                name="idp_sso_url"
                value={form.idp_sso_url}
                onChange={handleChange}
                required
                placeholder={ssoTranslations.ssoURLPlaceholder}
                style={{
                  ...styles.input,
                  ...(isFieldFromMetadata("idp_sso_url") ? {
                    backgroundColor: '#f0fdf4',
                    borderColor: '#bbf7d0'
                  } : {})
                }}
                readOnly={isFieldFromMetadata("idp_sso_url")}
                onFocus={(e) => !isFieldFromMetadata("idp_sso_url") && (e.target.style.borderColor = '#3b82f6')}
                onBlur={(e) => !isFieldFromMetadata("idp_sso_url") && (e.target.style.borderColor = '#d1d5db')}
              />
              <div style={styles.helpText}>
                {isFieldFromMetadata("idp_sso_url") 
                  ? ssoTranslations.extractedFromMetadataComplete
                  : ssoTranslations.idpSsoURLHelp
                }
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                {ssoTranslations.idpEntityID} *
                {isFieldFromMetadata("idp_entity_id") && (
                  <span style={{color: '#16a34a', fontSize: '0.75rem', marginLeft: '0.5rem'}}>
                    ✅ {ssoTranslations.fromMetadataURL}
                  </span>
                )}
              </label>
              <input
                name="idp_entity_id"
                value={form.idp_entity_id}
                onChange={handleChange}
                required
                placeholder={ssoTranslations.entityIDPlaceholder}
                style={{
                  ...styles.input,
                  ...(isFieldFromMetadata("idp_entity_id") ? {
                    backgroundColor: '#f0fdf4',
                    borderColor: '#bbf7d0'
                  } : {})
                }}
                readOnly={isFieldFromMetadata("idp_entity_id")}
                onFocus={(e) => !isFieldFromMetadata("idp_entity_id") && (e.target.style.borderColor = '#3b82f6')}
                onBlur={(e) => !isFieldFromMetadata("idp_entity_id") && (e.target.style.borderColor = '#d1d5db')}
              />
              <div style={styles.helpText}>
                {isFieldFromMetadata("idp_entity_id") 
                  ? ssoTranslations.extractedFromMetadataComplete
                  : ssoTranslations.idpEntityIDHelp
                }
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>{ssoTranslations.idpSingleLogoutURL}</label>
              <input
                name="idp_slo_url"
                value={form.idp_slo_url}
                onChange={handleChange}
                placeholder={ssoTranslations.sloURLPlaceholder}
                style={styles.input}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
              <div style={styles.helpText}>
                {ssoTranslations.sloURLHelp}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                {ssoTranslations.x509Certificate} *
                {isFieldFromMetadata("x509_certificate") && (
                  <span style={{color: '#16a34a', fontSize: '0.75rem', marginLeft: '0.5rem'}}>
                    ✅ {ssoTranslations.fromMetadataURL}
                  </span>
                )}
              </label>
              {isFieldFromMetadata("x509_certificate") ? (
                <div style={{
                  ...styles.textarea,
                  backgroundColor: '#f0fdf4',
                  borderColor: '#bbf7d0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '120px',
                  color: '#16a34a',
                  fontWeight: '600'
                }}>
                  🔐 {ssoTranslations.certificateImportedFromMetadataURL}
                </div>
              ) : (
                <textarea
                  name="x509_certificate"
                  value={form.x509_certificate}
                  onChange={handleChange}
                  required
                  placeholder="-----BEGIN CERTIFICATE-----
MIIDXTCCAkWgAwIBAgIJAKoK/heBjcOuMA0GCSqGSIb3DQEBBQUAMEUxCzAJBgNV
...
-----END CERTIFICATE-----"
                  style={styles.textarea}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              )}
              <div style={styles.helpText}>
                {isFieldFromMetadata("x509_certificate") 
                  ? ssoTranslations.certificateExtractedFromMetadata
                  : ssoTranslations.x509CertificateHelp
                }
              </div>
            </div>

            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                <span style={{...styles.stepNumber, fontSize: '0.75rem'}}>C</span>
                {ssoTranslations.configureAttributeMapping}
                {(metadataUrlSuccess || (pendingConn?.idp_sso_url && pendingConn?.idp_entity_id)) && (
                  <span style={{
                    marginLeft: '1rem',
                    fontSize: '0.75rem',
                    color: '#f59e0b',
                    backgroundColor: '#fffbeb',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    border: '1px solid #fed7aa'
                  }}>
                    ⚠️ {ssoTranslations.requiredToActivate}
                  </span>
                )}
              </div>
              <p style={styles.description}>
                {(metadataUrlSuccess || (pendingConn?.idp_sso_url && pendingConn?.idp_entity_id))
                  ? ssoTranslations.metadataProcessingComplete
                  : ssoTranslations.attributeMappingDescription
                }
              </p>
              
              {(metadataUrlSuccess || (pendingConn?.idp_sso_url && pendingConn?.idp_entity_id)) && (
                <div style={{
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  borderRadius: '6px',
                  padding: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{fontSize: '0.875rem', color: '#0369a1'}}>
                    <strong>📋 {ssoTranslations.progressStatus}</strong>
                    <div style={{marginTop: '0.5rem'}}>
                      ✅ {ssoTranslations.ssoURLExtractedFromMetadata}<br/>
                      ✅ {ssoTranslations.entityIDExtractedFromMetadata}<br/>
                      ✅ {ssoTranslations.certificateImportedFromMetadataStatus}<br/>
                      ⚠️ <strong>{ssoTranslations.attributeMappingNeededToActivate}</strong>
                    </div>
                  </div>
                </div>
              )}
              
              <div style={styles.warningBox}>
                <div style={styles.warningTitle}>📋 {ssoTranslations.commonAttributeNames}</div>
                <div style={styles.warningText}>
                  <strong>{ssoTranslations.emailAttributeOptions}</strong><br/>
                  <strong>{ssoTranslations.firstNameAttributeOptions}</strong><br/>
                  <strong>{ssoTranslations.lastNameAttributeOptions}</strong>
                </div>
              </div>

              <div style={styles.form}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{ssoTranslations.emailAttributeName} *</label>
                  <input
                    value={form.attribute_mapping.email}
                    onChange={(e) => setForm(f => ({
                      ...f,
                      attribute_mapping: {
                        ...f.attribute_mapping,
                        email: e.target.value
                      }
                    }))}
                    required
                    placeholder="email"
                    style={styles.input}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                  <div style={styles.helpText}>
                    {ssoTranslations.emailAttributeHelp}
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{ssoTranslations.firstNameAttributeName} *</label>
                  <input
                    value={form.attribute_mapping.first_name}
                    onChange={(e) => setForm(f => ({
                      ...f,
                      attribute_mapping: {
                        ...f.attribute_mapping,
                        first_name: e.target.value
                      }
                    }))}
                    required
                    placeholder="firstName"
                    style={styles.input}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                  <div style={styles.helpText}>
                    {ssoTranslations.firstNameAttributeHelp}
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{ssoTranslations.lastNameAttributeName} *</label>
                  <input
                    value={form.attribute_mapping.last_name}
                    onChange={(e) => setForm(f => ({
                      ...f,
                      attribute_mapping: {
                        ...f.attribute_mapping,
                        last_name: e.target.value
                      }
                    }))}
                    required
                    placeholder="lastName"
                    style={styles.input}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                  <div style={styles.helpText}>
                    {ssoTranslations.lastNameAttributeHelp}
                  </div>
                </div>
              </div>
            </div>

            {ssoError && (
              <div style={styles.errorText}>
                <strong>{ssoTranslations.configurationError}</strong> {ssoError}
              </div>
            )}

            <button
              type="button"
              onClick={handleSubmitIdp}
              disabled={isSavingIdp}
              style={{
                ...styles.primaryButton,
                ...(isSavingIdp ? styles.primaryButtonDisabled : {})
              }}
              onMouseEnter={!isSavingIdp ? (e) => e.target.style.backgroundColor = styles.primaryButtonHover.backgroundColor : null}
              onMouseLeave={!isSavingIdp ? (e) => e.target.style.backgroundColor = styles.primaryButton.backgroundColor : null}
            >
              {isSavingIdp ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid transparent',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  {ssoTranslations.activatingSSO}
                </>
              ) : (
                <>{(metadataUrlSuccess || (pendingConn?.idp_sso_url && pendingConn?.idp_entity_id)) ? '🚀 ' + ssoTranslations.completeSSOActivation : '✓ ' + ssoTranslations.activateSingleSignOn}</>
              )}
            </button>
          </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (ssoStatus === "loading") {
      return <div style={styles.loadingText}>{ssoTranslations.loadingSSOConfiguration}</div>;
    }

    if (ssoStatus === "error") {
      return (
        <div style={styles.card}>
          <div style={styles.errorText}>
            <strong>{ssoTranslations.errorLoadingSSO}</strong> {ssoError}
          </div>
          <button
            onClick={() => dispatch(getSSOStatus())}
            style={{...styles.secondaryButton, marginTop: '1rem'}}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            {ssoTranslations.tryAgain}
          </button>
        </div>
      );
    }

    if (ssoStatus === "active") {
      return (
        <div style={{...styles.card, ...styles.successCard}}>
          <h3 style={styles.successTitle}>
            ✅ {ssoTranslations.ssoActive}
          </h3>
          <p style={{...styles.description, color: '#16a34a'}}>
            {ssoTranslations.ssoActiveDescription}
          </p>

          {/* Configuration Details Section */}
          {activeConnection && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>
                📋 {ssoTranslations.ssoConfigurationDetails}
              </h4>

              {/* Service Provider Details */}
              <div style={{ marginBottom: '1rem' }}>
                <button
                  onClick={() => toggleSection('spDetails')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                    cursor: 'pointer'
                  }}
                >
                  <span>🔧 {ssoTranslations.serviceProviderDetails}</span>
                  <span>{expandedSections.spDetails ? '▼' : '▶'}</span>
                </button>
                
                {expandedSections.spDetails && (
                  <div style={{
                    border: '1px solid #e2e8f0',
                    borderTop: 'none',
                    borderRadius: '0 0 6px 6px',
                    padding: '1rem',
                    backgroundColor: '#ffffff'
                  }}>
                    {renderConfigItem(
                      ssoTranslations.singleSignOnURLACS,
                      activeConnection.acs_url,
                      'acs_url_active',
                      ssoTranslations.whereYourIdP
                    )}
                    {renderConfigItem(
                      ssoTranslations.audienceURISPEntityID,
                      activeConnection.audience_uri,
                      'audience_uri_active',
                      ssoTranslations.uniqueIdentifierForApplication
                    )}
                    {renderConfigItem(
                      ssoTranslations.nameIDFormat,
                      activeConnection.nameid_format,
                      'nameid_format_active',
                      ssoTranslations.howUserIdentitiesFormatted
                    )}
                  </div>
                )}
              </div>

              {/* Identity Provider Details */}
              <div style={{ marginBottom: '1rem' }}>
                <button
                  onClick={() => toggleSection('idpDetails')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                    cursor: 'pointer'
                  }}
                >
                  <span>🏢 {ssoTranslations.identityProviderConfiguration}</span>
                  <span>{expandedSections.idpDetails ? '▼' : '▶'}</span>
                </button>
                
                {expandedSections.idpDetails && (
                  <div style={{
                    border: '1px solid #e2e8f0',
                    borderTop: 'none',
                    borderRadius: '0 0 6px 6px',
                    padding: '1rem',
                    backgroundColor: '#ffffff'
                  }}>
                    {renderConfigItem(
                      ssoTranslations.idpSSOURL,
                      activeConnection.idp_sso_url,
                      'idp_sso_url_active',
                      ssoTranslations.yourIdentityProviderSignOn
                    )}
                    {renderConfigItem(
                      ssoTranslations.idpEntityID,
                      activeConnection.idp_entity_id,
                      'idp_entity_id_active',
                      ssoTranslations.yourIdentityProviderUnique
                    )}
                    {activeConnection.x509_certificate && renderConfigItem(
                      ssoTranslations.certificateMasked,
                      maskSensitiveData(activeConnection.x509_certificate, 12),
                      'x509_certificate_active',
                      ssoTranslations.x509CertificateSignature
                    )}
                    {activeConnection.attribute_mapping && (
                      <div style={styles.configItem}>
                        <div style={styles.configLabel}>{ssoTranslations.attributeMappings}</div>
                        <div style={styles.configValue}>
                          {Object.entries(activeConnection.attribute_mapping || {}).map(([key, value]) => 
                            `${key} → ${value}`
                          ).join(', ') || ssoTranslations.defaultMappings}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Connection Info */}
              <div style={{ marginBottom: '1rem' }}>
                <button
                  onClick={() => toggleSection('connectionInfo')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                    cursor: 'pointer'
                  }}
                >
                  <span>ℹ️ {ssoTranslations.connectionInformation}</span>
                  <span>{expandedSections.connectionInfo ? '▼' : '▶'}</span>
                </button>
                
                {expandedSections.connectionInfo && (
                  <div style={{
                    border: '1px solid #e2e8f0',
                    borderTop: 'none',
                    borderRadius: '0 0 6px 6px',
                    padding: '1rem',
                    backgroundColor: '#ffffff'
                  }}>
                    <div style={styles.configItem}>
                      <div style={styles.configLabel}>Status</div>
                      <div style={styles.configValue}>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: '#dcfce7',
                          color: '#166534',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          ✅ ACTIVE
                        </span>
                      </div>
                    </div>
                    {renderConfigItem(
                      ssoTranslations.connectionID,
                      activeConnection.connection_id,
                      'connection_id_active',
                      ssoTranslations.connectionIDHelp
                    )}
                    {activeConnection.created_at && (
                      <div style={styles.configItem}>
                        <div style={styles.configLabel}>{ssoTranslations.created}</div>
                        <div style={styles.configValue}>{formatDate(activeConnection.created_at)}</div>
                      </div>
                    )}
                    {activeConnection.updated_at && (
                      <div style={styles.configItem}>
                        <div style={styles.configLabel}>{ssoTranslations.lastUpdated}</div>
                        <div style={styles.configValue}>{formatDate(activeConnection.updated_at)}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Edit/Delete Actions */}
          {activeConnection && (
            <div style={styles.samlEditDeleteButtonGroup}>
              {!isInEditMode ? (
                <div style={styles.samlEditButtonRow}>
                  <button
                    onClick={handleEditConnection}
                    disabled={isEditingConnection || isDeletingConnection}
                    style={{
                      ...styles.samlEditButton,
                      ...(isEditingConnection || isDeletingConnection ? styles.buttonDisabled : {})
                    }}
                    onMouseEnter={!isEditingConnection && !isDeletingConnection ? (e) => e.target.style.backgroundColor = styles.samlEditButtonHover.backgroundColor : null}
                    onMouseLeave={!isEditingConnection && !isDeletingConnection ? (e) => e.target.style.backgroundColor = styles.samlEditButton.backgroundColor : null}
                  >
                    ✏️ {ssoTranslations.editConfiguration}
                  </button>
                  <button
                    onClick={handleDeleteConnection}
                    disabled={isEditingConnection || isDeletingConnection}
                    style={{
                      ...styles.samlDeleteButton,
                      ...(isEditingConnection || isDeletingConnection ? styles.buttonDisabled : {})
                    }}
                    onMouseEnter={!isEditingConnection && !isDeletingConnection ? (e) => e.target.style.backgroundColor = styles.samlDeleteButtonHover.backgroundColor : null}
                    onMouseLeave={!isEditingConnection && !isDeletingConnection ? (e) => e.target.style.backgroundColor = styles.samlDeleteButton.backgroundColor : null}
                  >
                    🗑️ {ssoTranslations.deleteConnection}
                  </button>
                </div>
              ) : (
                <div style={styles.samlEditForm}>
                  <div style={styles.samlEditFormTitle}>
                    ✏️ {ssoTranslations.editSAMLConfiguration}
                  </div>
                  
                  {/* Edit Form Fields */}
                  <div style={styles.samlEditFormGroup}>
                    <label style={styles.samlEditLabel}>{ssoTranslations.idpSingleSignOnURL} *</label>
                    <input
                      name="idp_sso_url"
                      value={editForm.idp_sso_url}
                      onChange={handleEditFormChange}
                      required
                      placeholder={ssoTranslations.ssoURLPlaceholder}
                      style={styles.samlEditInput}
                      onFocus={(e) => {
                        Object.assign(e.target.style, styles.samlEditInputFocus);
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  <div style={styles.samlEditFormGroup}>
                    <label style={styles.samlEditLabel}>{ssoTranslations.idpEntityID} *</label>
                    <input
                      name="idp_entity_id"
                      value={editForm.idp_entity_id}
                      onChange={handleEditFormChange}
                      required
                      placeholder={ssoTranslations.entityIDPlaceholder}
                      style={styles.samlEditInput}
                      onFocus={(e) => {
                        Object.assign(e.target.style, styles.samlEditInputFocus);
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  <div style={styles.samlEditFormGroup}>
                    <label style={styles.samlEditLabel}>{ssoTranslations.idpSingleLogoutURL}</label>
                    <input
                      name="idp_slo_url"
                      value={editForm.idp_slo_url}
                      onChange={handleEditFormChange}
                      placeholder={ssoTranslations.sloURLPlaceholderOptional}
                      style={styles.samlEditInput}
                      onFocus={(e) => {
                        Object.assign(e.target.style, styles.samlEditInputFocus);
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  <div style={styles.samlEditFormGroup}>
                    <label style={styles.samlEditLabel}>{ssoTranslations.x509Certificate} *</label>
                    <textarea
                      name="x509_certificate"
                      value={editForm.x509_certificate}
                      onChange={handleEditFormChange}
                      required
                      placeholder="-----BEGIN CERTIFICATE-----&#10;MIIDXTCCAkWgAwIBAgIJAKoK/OvGOwvUMA0GCSqGSIb3DQEBBQUAMEUxCzAJBgNV...&#10;-----END CERTIFICATE-----"
                      style={styles.samlEditTextarea}
                      onFocus={(e) => {
                        Object.assign(e.target.style, styles.samlEditTextareaFocus);
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Attribute Mapping */}
                  <div style={styles.samlEditFormGroup}>
                    <label style={styles.samlEditLabel}>{ssoTranslations.emailAttributeName} *</label>
                    <input
                      name="attribute_mapping.email"
                      value={editForm.attribute_mapping.email}
                      onChange={handleEditFormChange}
                      required
                      placeholder="email"
                      style={styles.samlEditInput}
                      onFocus={(e) => {
                        Object.assign(e.target.style, styles.samlEditInputFocus);
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  <div style={styles.samlEditFormGroup}>
                    <label style={styles.samlEditLabel}>{ssoTranslations.firstNameAttributeName} *</label>
                    <input
                      name="attribute_mapping.first_name"
                      value={editForm.attribute_mapping.first_name}
                      onChange={handleEditFormChange}
                      required
                      placeholder="firstName"
                      style={styles.samlEditInput}
                      onFocus={(e) => {
                        Object.assign(e.target.style, styles.samlEditInputFocus);
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  <div style={styles.samlEditFormGroup}>
                    <label style={styles.samlEditLabel}>{ssoTranslations.lastNameAttributeName} *</label>
                    <input
                      name="attribute_mapping.last_name"
                      value={editForm.attribute_mapping.last_name}
                      onChange={handleEditFormChange}
                      required
                      placeholder="lastName"
                      style={styles.samlEditInput}
                      onFocus={(e) => {
                        Object.assign(e.target.style, styles.samlEditInputFocus);
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Error Display */}
                  {samlEditError && (
                    <div style={styles.errorText}>
                      <strong>{ssoTranslations.editError}</strong> {samlEditError}
                    </div>
                  )}

                  {/* Form Actions */}
                  <div style={styles.samlEditFormActions}>
                    <button
                      onClick={handleCancelEdit}
                      disabled={isEditingConnection}
                      style={{
                        ...styles.cancelButton,
                        ...(isEditingConnection ? styles.buttonDisabled : {})
                      }}
                      onMouseEnter={!isEditingConnection ? (e) => e.target.style.backgroundColor = styles.cancelButtonHover.backgroundColor : null}
                      onMouseLeave={!isEditingConnection ? (e) => e.target.style.backgroundColor = styles.cancelButton.backgroundColor : null}
                    >
                      {ssoTranslations.cancel}
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      disabled={isEditingConnection || !editForm.idp_sso_url || !editForm.idp_entity_id || !editForm.x509_certificate}
                      style={{
                        ...styles.saveButton,
                        ...(isEditingConnection || !editForm.idp_sso_url || !editForm.idp_entity_id || !editForm.x509_certificate ? styles.saveButtonDisabled : {})
                      }}
                      onMouseEnter={!isEditingConnection && editForm.idp_sso_url && editForm.idp_entity_id && editForm.x509_certificate ? (e) => e.target.style.backgroundColor = styles.saveButtonHover.backgroundColor : null}
                      onMouseLeave={!isEditingConnection && editForm.idp_sso_url && editForm.idp_entity_id && editForm.x509_certificate ? (e) => e.target.style.backgroundColor = styles.saveButton.backgroundColor : null}
                    >
                      {isEditingConnection ? (
                        <>
                          <div style={styles.loadingSpinner} />
                          {ssoTranslations.saving}
                        </>
                      ) : (
                        '💾 ' + ssoTranslations.saveChanges
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{
            backgroundColor: '#dcfce7',
            border: '1px solid #bbf7d0',
            borderRadius: '6px',
            padding: '1rem',
            marginTop: '1rem'
          }}>
            <div style={{fontSize: '0.875rem', color: '#15803d'}}>
              <strong>{ssoTranslations.nextSteps}</strong>
            </div>
          </div>
        </div>
      );
    }

    if (ssoStatus === "pending") {
      return renderPending();
    }

    // not_configured
    return (
      <div style={styles.card}>
        <h3 style={styles.title}>
          <div style={styles.stepNumber}>1</div>
          {ssoTranslations.enableSingleSignOn}
        </h3>
        <p style={styles.description}>
          {ssoTranslations.allowYourTeam}
        </p>
        
        <div style={{
          backgroundColor: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: '6px',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{fontSize: '0.875rem', color: '#0369a1'}}>
            <strong>{ssoTranslations.howItWorks}</strong>
          </div>
        </div>

        {isInitiating && (
          <div style={{
            ...styles.warningBox,
            backgroundColor: '#f0f9ff',
            borderColor: '#bae6fd'
          }}>
            <div style={{...styles.warningTitle, color: '#0369a1'}}>
              {ssoTranslations.settingUpSAMLConnectionDots}
            </div>
            <div style={{...styles.warningText, color: '#075985'}}>
              {ssoTranslations.thisMayTakeAMoment}
            </div>
          </div>
        )}

        {ssoError && !isInitiating && (
          <div style={styles.errorText}>
            <strong>{ssoTranslations.setupError}</strong> {ssoError}
          </div>
        )}

        <button
          onClick={handleEnableSSO}
          disabled={isInitiating}
          style={{
            ...styles.primaryButton,
            ...(isInitiating ? styles.primaryButtonDisabled : {})
          }}
          onMouseEnter={!isInitiating ? (e) => e.target.style.backgroundColor = styles.primaryButtonHover.backgroundColor : null}
          onMouseLeave={!isInitiating ? (e) => e.target.style.backgroundColor = styles.primaryButton.backgroundColor : null}
        >
          {isInitiating ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid transparent',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              {ssoTranslations.settingUp}
            </>
          ) : (
            ssoTranslations.startSSOSetup
          )}
        </button>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <h2 style={{...styles.title, fontSize: '2rem', textAlign: 'center', marginBottom: '2rem'}}>
        {ssoTranslations.pageTitle}
      </h2>
      
  

      {renderContent()}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeletingConnection}
        title={ssoTranslations.deleteSAMLConnection}
        message={ssoTranslations.deleteSAMLMessage}
        confirmationText="delete"
        itemName={ssoTranslations.samlConnection}
      />
    </div>
  );
}

export default SSOManagement;